import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import VendorMap, { resolveTentMode } from "../components/VendorMap";
import { SlotVendorsModal, SpotlightModal } from "../components/VendorModals";
import { useVendorData } from "../components/Hooks/useVendorData";
import slotsConfig from "../components/SlotsConfig_Summer2026";
import "./VendorMapPage.css";

const MAX_SUGGESTIONS = 6;

function FloatingSearch({ vendors, onSelectVendor }) {
  const [query,   setQuery]   = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return vendors
      .filter(v =>
        v.brand_name.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q)
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [vendors, query]);

  const showDropdown = focused && query.trim().length > 0;

  const handleSelect = vendor => {
    onSelectVendor(vendor);
    setQuery("");
    inputRef.current?.blur();
  };

  return (
    <div className="vmp-float-search">
      <div className="vmp-float-search-bar">
        <svg
          className="vmp-float-search-icon"
          width="15" height="15" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7"/>
          <path d="M21 21l-4.35-4.35"/>
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Search vendors…"
          aria-label="Search vendors"
          className="vmp-float-search-input"
        />
        {query && (
          <button
            type="button"
            className="vmp-float-search-clear"
            aria-label="Clear search"
            // onMouseDown (not onClick) so this fires before the input's
            // onBlur would otherwise close the dropdown first
            onMouseDown={e => { e.preventDefault(); setQuery(""); inputRef.current?.focus(); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="vmp-float-search-suggestions" role="listbox">
          {suggestions.length === 0 ? (
            <div className="vmp-float-search-empty">No vendors match “{query}”</div>
          ) : (
            suggestions.map(v => (
              <button
                key={v.slotId ? `${v.slotId}-${v.brand_name}` : v.brand_name}
                type="button"
                className="vmp-float-search-item"
                role="option"
                // onMouseDown fires before the input's onBlur, so the
                // click registers before the dropdown would otherwise
                // close out from under it
                onMouseDown={e => { e.preventDefault(); handleSelect(v); }}
              >
                <span className="vmp-float-search-item-logo">
                  {v.logo_url ? (
                    <img src={v.logo_url} alt="" className="vmp-float-search-item-logo-img"/>
                  ) : (
                    v.brand_name?.[0]?.toUpperCase()
                  )}
                </span>
                <span className="vmp-float-search-item-text">
                  <span className="vmp-float-search-item-name">{v.brand_name}</span>
                  {v.slotId && <span className="vmp-float-search-item-slot">Slot {v.slotId}</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function VendorMapPage() {
  const {
    slots,
    reservedSlots,
    loading,
    error,
    allVendors,
    catPalette,
  } = useVendorData();

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [activeTile,  setActiveTile]  = useState(null);
  const [spotlight,   setSpotlight]   = useState(null);
  // slotList: { slotId, vendors, effectiveType } | null
  // We keep this in a ref too so that "cameFromList" detection is reliable
  // even after setSlotList(null) is called on the path to spotlight.
  const [slotList,    setSlotList]    = useState(null);
  const slotListRef   = useRef(null);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const openSpotlight = useCallback((vendor, slotId) => {
    setActiveTile(slotId);
    setSpotlight({ ...vendor, slotId });
  }, []);

  const closeAll = useCallback(() => {
    setSpotlight(null);
    setSlotList(null);
    slotListRef.current = null;
    setActiveTile(null);
  }, []);

  const handleTileClick = useCallback(slotId => {
    const bookings = slots[slotId];
    if (!bookings?.length) return;

    if (bookings.length === 1) {
      // Single vendor — go straight to spotlight
      setSlotList(null);
      slotListRef.current = null;
      openSpotlight(bookings[0], slotId);
    } else {
      // Multi-vendor — show the shaped floor plan first.
      // resolveTentMode needs the slot's static config (type, id).
      const slotDef = slotsConfig.find(s => s.id === slotId);
      const effectiveType = slotDef ? resolveTentMode(slotDef, bookings) : null;
      const facing = slotDef?.facing;

      setSpotlight(null);
      setActiveTile(slotId);
      const list = { slotId, vendors: bookings, effectiveType, facing };
      setSlotList(list);
      slotListRef.current = list;
    }
  }, [slots, openSpotlight]);

  // From the floating search dropdown
  const handleSelectVendor = useCallback(vendor => {
    setSlotList(null);
    slotListRef.current = null;
    openSpotlight(vendor, vendor.slotId);
  }, [openSpotlight]);

  // From SlotVendorsModal → clicking a filled floor-plan cell
  // We keep slotList in ref so the back button still works after setSlotList(null)
  const handleSlotVendorSelect = useCallback(vendor => {
    // Don't null slotList here — keep it so cameFromList stays true
    // We hide the list modal by checking spotlight !== null in render
    openSpotlight(vendor, slotListRef.current?.slotId);
  }, [openSpotlight]);

  const handleBackToList = useCallback(() => {
    // Re-show the slot list, clear spotlight
    if (slotListRef.current) setSlotList(slotListRef.current);
    setSpotlight(null);
  }, []);

  // Escape key
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") closeAll(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [closeAll]);

  // cameFromList: spotlight was opened from a multi-vendor slot list
  const cameFromList = !!(
    spotlight &&
    slotListRef.current &&
    slotListRef.current.slotId === spotlight.slotId &&
    slotListRef.current.vendors.length > 1
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  // Always full-bleed / "fullscreen" — no card shell, no header bar, no
  // event-title/stats badge. Just the map, with a small glassy floating
  // search bar (top-left on desktop, bottom-center on mobile — see CSS)
  // as the only chrome over it.
  return (
    <div className="vmp-page">

      <FloatingSearch vendors={allVendors} onSelectVendor={handleSelectVendor}/>

      {/* ── Map — fills the entire page ──────────────────────────────── */}
      <main className="vmp-page-map-wrap">
        <VendorMap
          slots={slots}
          reservedSlots={reservedSlots}
          loading={loading}
          error={error}
          activeTile={activeTile}
          onTileClick={handleTileClick}
        />
      </main>

      {/* ── Modals (page-level, above everything) ─────────────────────── */}
      {slotList && !spotlight && (
        <SlotVendorsModal
          slotId={slotList.slotId}
          vendors={slotList.vendors}
          effectiveType={slotList.effectiveType}
          facing={slotList.facing}
          catPalette={catPalette}
          onSelect={handleSlotVendorSelect}
          onClose={closeAll}
        />
      )}

      <SpotlightModal
        vendor={spotlight}
        catPalette={catPalette}
        cameFromList={cameFromList}
        onBack={handleBackToList}
        onClose={closeAll}
      />
    </div>
  );
}